import { motion } from "framer-motion";
import { Download, FileDown, FileText, PlusCircle, Receipt, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { NpTable } from "../../components/np-table";
import {
  useDownloadCnabBaixa,
  useDownloadNp,
  useGerarBaixaNp,
  useNotasPromissorias,
  useRegerarCompraNp,
} from "../../hooks/use-nota-promissoria";
import { StatusFiltro } from "../../types";
import { cnabBaixaPronto, documentosProntos, resumoAlerta } from "../../utils/vencimento";

export default function NotaPromissoriaControlePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFiltro>("ativas");
  const [selecionadas, setSelecionadas] = useState<number[]>([]);
  const [confirmarBaixa, setConfirmarBaixa] = useState(false);
  const [confirmarRegerar, setConfirmarRegerar] = useState(false);

  const { data: notas = [], isLoading, refetch } = useNotasPromissorias(status);
  const gerarBaixa = useGerarBaixaNp();
  const download = useDownloadNp();
  const downloadBaixa = useDownloadCnabBaixa();
  const regerarCompra = useRegerarCompraNp();

  const { vencidas, aVencer } = useMemo(() => resumoAlerta(notas), [notas]);

  const selecionadasNotas = notas.filter((n) => selecionadas.includes(n.id));
  // Gerar o CNAB de baixa não depende das flags: o arquivo pode ser refeito a
  // qualquer momento, inclusive para uma NP já marcada como comprada ou baixada.
  const podeBaixar = selecionadasNotas.length > 0;
  const podeDownload =
    selecionadasNotas.length > 0 && selecionadasNotas.every(documentosProntos);
  // Independente do anterior: o CNAB de baixa não depende dos documentos da compra.
  const podeDownloadBaixa =
    selecionadasNotas.length > 0 && selecionadasNotas.every(cnabBaixaPronto);

  const trocarFiltro = (valor: string) => {
    setStatus(valor as StatusFiltro);
    setSelecionadas([]);
  };

  return (
    <div className="p-6 space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <motion.div
                className="p-2 rounded-lg bg-primary/10"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <Receipt className="h-6 w-6 text-primary" />
              </motion.div>
              <h1 className="text-3xl font-bold text-foreground bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Controle de Notas Promissórias
              </h1>
            </div>
            <p className="text-muted-foreground mt-1 ml-14">
              Acompanhe vencimentos, marque compra e baixa, gere o CNAB de baixa e baixe os documentos
            </p>
          </div>
          <Button onClick={() => navigate("/notas-promissorias/criar")}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Criar NP
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={status} onValueChange={trocarFiltro}>
              <TabsList>
                <TabsTrigger value="ativas">Ativas</TabsTrigger>
                <TabsTrigger value="inativas">Inativas (baixadas)</TabsTrigger>
                <TabsTrigger value="todas">Todas</TabsTrigger>
              </TabsList>
            </Tabs>
            {status !== "inativas" && (
              <div className="flex items-center gap-2 text-sm">
                {vencidas.length > 0 && <Badge variant="destructive">{vencidas.length} vencida(s)</Badge>}
                {aVencer.length > 0 && (
                  <Badge className="bg-amber-500 text-white hover:bg-amber-500/90">
                    {aVencer.length} vence(m) em 15 dias
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {selecionadas.length > 0 ? `${selecionadas.length} selecionada(s)` : "Selecione NPs para agir em lote"}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!podeDownload || download.isPending}
              onClick={() => download.mutate(selecionadas)}
              title="ZIP com termo de cessão, NP e CNAB de remessa das selecionadas"
            >
              <Download className="h-4 w-4 mr-1" />
              Baixar documentos
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!podeDownloadBaixa || downloadBaixa.isPending}
              onClick={() => downloadBaixa.mutate(selecionadas)}
              title="Só o CNAB de baixa das selecionadas"
            >
              <FileText className="h-4 w-4 mr-1" />
              Baixar CNAB de baixa
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selecionadas.length === 0 || regerarCompra.isPending}
              onClick={() => setConfirmarRegerar(true)}
              title="Refaz CNAB de remessa, termo de cessão e cártula das selecionadas"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${regerarCompra.isPending ? "animate-spin" : ""}`} />
              Regerar documentos
            </Button>
            <Button
              size="sm"
              disabled={!podeBaixar || gerarBaixa.isPending}
              onClick={() => setConfirmarBaixa(true)}
              title="Gera o CNAB de baixa das NPs selecionadas"
            >
              <FileDown className="h-4 w-4 mr-1" />
              Gerar CNAB de baixa
            </Button>
          </div>
        </div>

        <NpTable
          data={notas}
          loading={isLoading}
          selecionadas={selecionadas}
          onSelecionadasChange={setSelecionadas}
          onRefresh={() => refetch()}
        />
      </motion.div>

      <ConfirmDialog
        open={confirmarBaixa}
        onOpenChange={setConfirmarBaixa}
        title="Gerar CNAB de baixa"
        description={`Gerar o CNAB de baixa para ${selecionadasNotas.length} NP (${selecionadasNotas
          .map((n) => n.id_contrato)
          .join(", ")})? O arquivo é gravado no S3 e sai por aqui, no download dos documentos — nada é enviado por e-mail. A flag "baixado" continua manual.`}
        onConfirm={() => {
          // A seleção fica: assim que o CNAB de baixa sai, "Baixar documentos"
          // age sobre as mesmas NPs, sem obrigar a selecionar tudo de novo.
          gerarBaixa.mutate(selecionadas);
        }}
        confirmDisabled={gerarBaixa.isPending}
      />

      <ConfirmDialog
        open={confirmarRegerar}
        onOpenChange={setConfirmarRegerar}
        title="Regerar documentos de compra"
        description={`Refazer CNAB de remessa, termo de cessão e cártula de ${
          selecionadasNotas.length
        } NP (${selecionadasNotas.map((n) => n.id_contrato).join(", ")})? Os arquivos são
        reescritos no mesmo caminho do S3, com a data de emissão original — nada é
        renumerado nem reemitido. Use quando a geração falhou e os ícones de documentos
        seguem vermelhos.`}
        onConfirm={() => regerarCompra.mutate(selecionadas)}
        confirmDisabled={regerarCompra.isPending}
      />
    </div>
  );
}
